import React, { useContext, useState, useEffect } from "react";

import {
  IAddItemPanelComponentProps,
  ISearchProductPayload,
} from "./AddItemPanelComponent.types";
import {
  IProductSearchRequest,
  IProductSearchResult,
  IApiResponse,
  IProductSearchResponse,
} from "./ProductSearch.types";
import {
  CoherencePanel,
  LoadingState,
} from "@coherence-design-system/controls";
import {
  ADD_ACTION,
  DefaultItemFormData,
  InitialStateOfResponseMessage,
  ItemErrorsDefaultState,
} from "../../Common/QuotationData";
import {
  DefaultButton,
  Icon,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  SearchBox,
  Separator,
  Stack,
  TextField,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";
import { IItemErrors } from "../../Common/Interfaces/IItemErrors";
import {
  IItemDetailEntity,
  IResponseMessage,
  LineItemType,
  QuotationInfoType,
} from "../../Quotation.types";
import {
  OverridePanelPaddingStyle,
  SeparatorStyles,
  buttonStyleWithWidth100,
  columnPropsWithHeight150,
  searchBoxStylesWithHiddenIcon,
  stackTokensWithGap5,
  textFieldGray,
  textFieldStyles300,
} from "../../QuotationStyle";
import { appInsights } from "../../../../services/Logging/AppInsights";
import { QuotationDetailService } from "../../../../services/Quotations/QuotationDetailService";
import { QuotationContext } from "../../QuotationContext";
import { BundleSelection } from "../BundleSelection/BundleSelection";
import { useNavigate } from "react-router-dom";
import { ISearchSkuResponse } from "../../../../common/Interfaces/ISearchSkuResponse";
import { FeatureFlags } from "../../../../config";

export const AddItemPanelComponent: React.FunctionComponent<
  IAddItemPanelComponentProps
> = (props) => {
  const navigate = useNavigate();
  const { isItemPanelOpen, dismissItemPanel } = props;
  const [itemErrors, setItemErrors] = useState<IItemErrors>(
    ItemErrorsDefaultState
  );
  const [errorMessage, setErrorMessage] = useState<IResponseMessage>(
    InitialStateOfResponseMessage
  );
  const [itemLoading, setItemLoading] = useState<boolean>(false);
  const [searchingSku, setSearchingSku] = useState<string>("");
  const [itemFormData, setItemFormData] =
    useState<IItemDetailEntity>(DefaultItemFormData);  const [searchedItem, setSearchedItem] = useState<
    ISearchSkuResponse | undefined
  >();
  // Enhanced search state - supports backend pagination
  const [searchResults, setSearchResults] = useState<IProductSearchResult[]>([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState<boolean>(false);
  const [isCommittedSearch, setIsCommittedSearch] = useState<boolean>(false);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalResults, setTotalResults] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [hasPreviousPage, setHasPreviousPage] = useState<boolean>(false);
  const [expandedTitles, setExpandedTitles] = useState<Set<string>>(new Set());
  
  // Filtering and Sorting State
  const [sortBy, setSortBy] = useState<string>(""); // "", "name", "inventory", "screenSize", "processor"
  const [sortOrder, setSortOrder] = useState<string>("desc"); // "asc", "desc"
  const [screenSizeFilter, setScreenSizeFilter] = useState<string>("all");
  const [processorFilter, setProcessorFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sapIdFilter, setSapIdFilter] = useState<string>("all");
  const [customSapId, setCustomSapId] = useState<string>("");
  const [showFilters, setShowFilters] = useState<boolean>(false);
  
  // Additional filter states for remaining fields
  const [productTitleFilter, setProductTitleFilter] = useState<string>("");
  const [productIdFilter, setProductIdFilter] = useState<string>("");

  const [preOrderFilter, setPreOrderFilter] = useState<string>("all");
  const [colorFilter, setColorFilter] = useState<string>("all");
  
  const resultsPerPage = 50;
  
  // Handle click outside to close search dropdown
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (showSearchDropdown) {
        const searchContainer = document.querySelector('.search-results-dropdown');
        const searchBox = document.querySelector('.ms-SearchBox');
        const searchIcon = document.querySelector('[data-search-icon="true"]');
        
        const target = event.target as Node;
        const isInsideSearchContainer = searchContainer && searchContainer.contains(target);
        const isInsideSearchBox = searchBox && searchBox.contains(target);
        const isInsideSearchIcon = searchIcon && searchIcon.contains(target);
        
        if (!isInsideSearchContainer && !isInsideSearchBox && !isInsideSearchIcon) {
          setShowSearchDropdown(false);
        }
      }
    };

    if (showSearchDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showSearchDropdown]);
  
  const { quotationInfo, setQuotationInfo } = useContext(
    QuotationContext
  ) as QuotationInfoType;
  
  // Enhanced search function with backend pagination support
  const performProductSearch = async (searchTerm: string, page: number = 1) => {
    console.log("performProductSearch called for Enter key with:", searchTerm, "page:", page);
    
    // Check if Product Search feature is enabled
    if (!FeatureFlags.enableProductSearch) {
      console.log("Feature disabled in performProductSearch");
      // Feature disabled - clear search results and hide dropdown
      setSearchResults([]);
      setTotalResults(0);
      setCurrentPage(1);
      setTotalPages(0);
      setHasNextPage(false);
      setHasPreviousPage(false);
      setShowSearchDropdown(false);
      setSearchLoading(false);
      return;
    }

    const trimmedTerm = searchTerm.trim();
    console.log("Trimmed term:", trimmedTerm, "length:", trimmedTerm.length);
    if (trimmedTerm.length >= 3) {
      console.log("Starting search loading state");
      setSearchLoading(true);
      try {
        const searchRequest: IProductSearchRequest = {
          searchTerm: trimmedTerm,
          market: quotationInfo.customer.customerMarket ?? "US",
          languageCode: quotationInfo.customer.languageCode ?? "en-US",
          customerId: quotationInfo.customer.id,
          pageNumber: page,
          pageSize: resultsPerPage,
          maxResults: 1000 // Higher limit for backend pagination
        };
        
        console.log("About to call QuotationDetailService.searchProducts with:", searchRequest);
        const response = await QuotationDetailService.searchProducts(searchRequest);
        console.log("Enter key search - API call made to BigCat/SearchProducts");
        console.log("Response received:", response);
          if (response.data && response.data.code === 200) {
          
          if (response.data?.data?.searchResults?.length > 0) {
            setSearchResults(response.data.data.searchResults);
            setTotalResults(response.data.data.totalResults || response.data.data.searchResults.length);
            setCurrentPage(response.data.data.currentPage || page);
            setTotalPages(response.data.data.totalPages || Math.ceil((response.data.data.totalResults || response.data.data.searchResults.length) / resultsPerPage));
            setHasNextPage(response.data.data.hasNextPage || (response.data.data.currentPage || page) < (response.data.data.totalPages || Math.ceil((response.data.data.totalResults || response.data.data.searchResults.length) / resultsPerPage)));
            setHasPreviousPage(response.data.data.hasPreviousPage || (response.data.data.currentPage || page) > 1);
            setShowSearchDropdown(true);
            appInsights.trackEvent({
              name: "Enhanced Product Search - Results Retrieved",
              properties: { 
                searchTerm: trimmedTerm, 
                resultCount: response.data.data.searchResults.length,
                currentPage: page,
                totalPages: response.data.data.totalPages || Math.ceil(response.data.data.totalResults / resultsPerPage)
              },
            });
          } else {
            // Show "No results found" in the dropdown
            setSearchResults([]);
            setTotalResults(0);
            setCurrentPage(1);
            setTotalPages(0);
            setHasNextPage(false);
            setHasPreviousPage(false);
            setShowSearchDropdown(true); // Keep dropdown visible to show no results message
            appInsights.trackEvent({
              name: "Enhanced Product Search - No Results",
              properties: { searchTerm: trimmedTerm, page: page }
            });
          }
        } else {
          setSearchResults([]);
          setTotalResults(0);
          setCurrentPage(1);
          setTotalPages(0);
          setHasNextPage(false);
          setHasPreviousPage(false);
          setShowSearchDropdown(false);
        }
      } catch (error) {
        appInsights.trackException({
          error: error as Error,
          properties: {
            name: "Enhanced Product Search Error",
            searchTerm: trimmedTerm,
            page: page
          },
        });
        setSearchResults([]);
        setTotalResults(0);
        setCurrentPage(1);
        setTotalPages(0);
        setHasNextPage(false);
        setHasPreviousPage(false);
        setShowSearchDropdown(false);
      } finally {
        setSearchLoading(false);
      }
    } else {
      setSearchResults([]);
      setTotalResults(0);
      setCurrentPage(1);
      setTotalPages(0);
      setHasNextPage(false);
      setHasPreviousPage(false);
      setShowSearchDropdown(false);
      setSearchLoading(false);
    }
  };

  // Search Icon functionality - uses ProductSkuAvailability endpoint
  const performProductSearchIcon = async (searchTerm: string, page: number = 1) => {
    console.log("performProductSearchIcon called with:", searchTerm, "page:", page);
    const trimmedTerm = searchTerm.trim();
    if (trimmedTerm.length >= 3) {
      setSearchLoading(true);
      try {
        const searchProductPayload: ISearchProductPayload = {
          sapId: trimmedTerm,
          bigId: "",
          market: quotationInfo.customer.customerMarket ?? "US",
          languageCode: quotationInfo.customer.languageCode ?? "en-US",
          customerId: quotationInfo.customer.id
        };

        const response = await QuotationDetailService.searchProductsIcon(searchProductPayload);
        
        // ProductSkuAvailability returns direct product details, not search results
        if (response.data?.data?.deviceType && response.data?.data?.deviceType !== null) {
          // Product found - navigate directly to BundleAndItemSelection
          setSearchedItem(response.data.data);
          setQuotationInfo({
            ...quotationInfo,
            searchedSku: trimmedTerm,
            skuData: response.data.data,
          });
          navigate("/BundleAndItemSelection");
          setItemLoading(false);
          setShowSearchDropdown(false);
          appInsights.trackEvent({
            name: "Enhanced Product Search Icon - Product Found, Navigating to Bundle Selection",
            properties: { 
              searchTerm: trimmedTerm,
              deviceType: response.data.data.deviceType
            }
          });
        } else {
          // Product not found - show error
          const updateItemErrors = {
            ...itemErrors,
            skuId: true,
          };
          setItemErrors(updateItemErrors);
          setSearchedItem(undefined);
          setItemLoading(false);
          setShowSearchDropdown(false);
          setErrorMessage({
            message: response.data.message || "Product not found",
            showMessage: true,
          });
          appInsights.trackEvent({
            name: "Enhanced Product Search Icon - Product Not Found",
            properties: { searchTerm: trimmedTerm }
          });
        }
      } catch (error) {
        const updateItemErrors = {
          ...itemErrors,
          skuId: true,
        };
        setItemErrors(updateItemErrors);
        appInsights.trackException({
          error: error as Error,
          properties: {
            name: "Enhanced Product Search Icon Error",
            searchTerm: trimmedTerm,
          },
        });
        setSearchedItem(undefined);
        setItemLoading(false);
        setShowSearchDropdown(false);
      } finally {
        setSearchLoading(false);
      }
    } else {
      setSearchResults([]);
      setTotalResults(0);
      setCurrentPage(1);
      setTotalPages(0);
      setHasNextPage(false);
      setHasPreviousPage(false);
      setShowSearchDropdown(false);
      setSearchLoading(false);
    }
  };
  
  // Handle search text change - only update input value, no automatic search
  const handleSearchChange = (event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) => {
    const searchTerm = newValue ?? "";
    setSearchingSku(searchTerm);
    
    if (searchTerm.length === 0) {
      // Clear results when search is cleared
      setSearchResults([]);
      setShowSearchDropdown(false);
      setIsCommittedSearch(false);
      setCurrentPage(1);
      setTotalResults(0);
      setTotalPages(0);
      setHasNextPage(false);
      setHasPreviousPage(false);
      setItemFormData(DefaultItemFormData);
      setItemLoading(false);
      
      // Reset all filters when search is cleared
      resetFilters();
    }
    // No automatic search - search only happens on Enter key press
  };

  // Handle Enter key press to trigger search
  const handleSearchSubmit = (searchTerm: string) => {
    console.log("handleSearchSubmit called - Enter key search triggered with:", searchTerm);
    if (searchTerm.trim().length >= 3) {
      console.log("Search term length valid, calling performProductSearch");
      
      // Reset filters for new search
      resetFilters();
      
      setIsCommittedSearch(true); // Mark as committed search
      performProductSearch(searchTerm, 1); // Always start from page 1 for new search
    } else {
      console.log("Search term too short, clearing results");
      setSearchResults([]);
      setShowSearchDropdown(false);
      setIsCommittedSearch(false);
      setCurrentPage(1);
      setTotalResults(0);
      setTotalPages(0);
      setHasNextPage(false);
      setHasPreviousPage(false);
    }
  };

  // Handle search icon click - uses ProductSkuAvailability endpoint
  const handleSearchIconClick = (searchTerm: string) => {
    console.log("Search icon clicked with term:", searchTerm);
    if (searchTerm.trim().length >= 3) {
      console.log("Search term valid, calling performProductSearchIcon");
      
      // Reset filters for new search
      resetFilters();
      
      setIsCommittedSearch(true); // Mark as committed search
      performProductSearchIcon(searchTerm, 1); // Always start from page 1 for new search
    } else {
      console.log("Search term too short, clearing results");
      setSearchResults([]);
      setShowSearchDropdown(false);
      setIsCommittedSearch(false);
      setCurrentPage(1);
      setTotalResults(0);
      setTotalPages(0);
      setHasNextPage(false);
      setHasPreviousPage(false);
    }
  };

  // Backend pagination: triggers new API call for each page
  const handlePageChange = async (page: number) => {
    if (searchingSku.trim().length >= 3) {
      // Check if backend supports pagination by looking at response structure
      const backendSupportsPagination = totalPages > 0 && searchResults.length <= resultsPerPage;
      
      if (backendSupportsPagination) {
        // True backend pagination - make new API call
        await performProductSearch(searchingSku, page);
      } else {
        // Fallback to client-side pagination if backend doesn't support it
        setCurrentPage(page);
      }
    }
  };

  // Client-side pagination helper (fallback when backend doesn't support pagination)
  const getPaginatedResults = () => {
    // Get filtered and sorted results first
    const filteredResults = getFilteredAndSortedResults();
    
    // If we have more results than page size, use client-side pagination
    if (filteredResults.length > resultsPerPage) {
      const startIndex = (currentPage - 1) * resultsPerPage;
      const endIndex = startIndex + resultsPerPage;
      return filteredResults.slice(startIndex, endIndex);
    }
    // Otherwise return all filtered results
    return filteredResults;
  };

  const getClientSideTotalPages = () => {
    return Math.ceil(searchResults.length / resultsPerPage);
  };

  // Toggle expanded state for product titles
  const toggleTitleExpansion = (uniqueId: string, event: React.MouseEvent) => {
    event.preventDefault(); // Prevent any default behavior
    event.stopPropagation(); // Prevent row selection when clicking expand button
    
    console.log('Toggling expansion for uniqueId:', uniqueId);
    console.log('Current expandedTitles:', Array.from(expandedTitles));
    
    const newExpandedTitles = new Set(expandedTitles);
    if (expandedTitles.has(uniqueId)) {
      newExpandedTitles.delete(uniqueId);
      console.log('Removing uniqueId from expansion:', uniqueId);
    } else {
      newExpandedTitles.add(uniqueId);
      console.log('Adding uniqueId to expansion:', uniqueId);
    }
    
    console.log('New expandedTitles:', Array.from(newExpandedTitles));
    setExpandedTitles(newExpandedTitles);
  };

  // Filtering and Sorting Functions
  const getUniqueValues = (results: IProductSearchResult[], field: keyof IProductSearchResult): string[] => {
    const values = results.map(item => String(item[field])).filter(Boolean);
    return Array.from(new Set(values)).sort();
  };


  const getFilteredAndSortedResults = (): IProductSearchResult[] => {
    let filtered = [...searchResults];

    // Apply existing filters
    if (screenSizeFilter !== "all") {
      filtered = filtered.filter(item => item.screenSize === screenSizeFilter);
    }
    if (processorFilter !== "all") {
      filtered = filtered.filter(item => item.processor === processorFilter);
    }
    if (statusFilter !== "all") {
      filtered = filtered.filter(item => item.inventoryStatus === statusFilter);
    }
    if (sapIdFilter !== "all") {
      filtered = filtered.filter(item => item.sapSkuId === sapIdFilter);
    }
    if (customSapId.trim() !== "") {
      filtered = filtered.filter(item => 
        item.sapSkuId.toLowerCase().includes(customSapId.toLowerCase().trim())
      );
    }

    // Apply new filters
    if (productTitleFilter.trim() !== "") {
      filtered = filtered.filter(item => 
        item.productTitle.toLowerCase().includes(productTitleFilter.toLowerCase().trim())
      );
    }
    if (productIdFilter.trim() !== "") {
      filtered = filtered.filter(item => 
        item.productId.toLowerCase().includes(productIdFilter.toLowerCase().trim())
      );
    }

    if (preOrderFilter !== "all") {
      const isPreOrder = preOrderFilter === "true";
      filtered = filtered.filter(item => item.isPreOrder === isPreOrder);
    }
    if (colorFilter !== "all") {
      filtered = filtered.filter(item => item.color === colorFilter);
    }

    // Apply sorting only if user has selected a sort option (not empty string)
    if (sortBy !== "") {
      filtered.sort((a, b) => {
        let aVal: any, bVal: any;
        
        switch (sortBy) {
          case "name":
            aVal = a.productTitle.toLowerCase();
            bVal = b.productTitle.toLowerCase();
            break;
          case "inventory":
            aVal = a.inventory;
            bVal = b.inventory;
            break;
          case "screenSize":
            aVal = parseFloat(a.screenSize.replace(/[^\d.]/g, '')) || 0;
            bVal = parseFloat(b.screenSize.replace(/[^\d.]/g, '')) || 0;
            break;
          case "processor":
            aVal = a.processor.toLowerCase();
            bVal = b.processor.toLowerCase();
            break;
          default:
            return 0; // No sorting for unknown sortBy values
        }

        if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
        if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }
    // If sortBy is empty, return filtered results in their natural order as returned by the service

    return filtered;
  };

  const resetFilters = () => {
    setScreenSizeFilter("all");
    setProcessorFilter("all");
    setStatusFilter("all");
    setSapIdFilter("all");
    setCustomSapId("");
    setProductTitleFilter("");
    setProductIdFilter("");

    setPreOrderFilter("all");
    setColorFilter("all");
    setSortBy(""); // Reset to natural order from service (empty string = no sorting)
    setSortOrder("desc"); // Keep descending order as default for when sorting is applied
  };

  // Handle product selection from dropdown
  const handleProductSelection = async (selectedProduct: IProductSearchResult) => {
    setSearchingSku(selectedProduct.sapSkuId);
    setShowSearchDropdown(false);
    setSearchResults([]);
    setCurrentPage(1);
    setTotalResults(0);
    setTotalPages(0);
    setHasNextPage(false);
    setHasPreviousPage(false);
    setItemLoading(true);

    try {
      appInsights.trackEvent({
        name: "Enhanced Product Search - Product Selected",
        properties: { 
          selectedProductId: selectedProduct.productId,
          selectedSku: selectedProduct.sapSkuId
        },
      });

      const searchProductPayload: ISearchProductPayload = {
        sapId: selectedProduct.sapSkuId,
        bigId: selectedProduct.productId,
        market: quotationInfo.customer.customerMarket ?? "US",
        languageCode: quotationInfo.customer.languageCode ?? "en-US",
        customerId: quotationInfo.customer.id
      };

      const res = await QuotationDetailService.getSearchedProductDetails(searchProductPayload);
      
      if (res.data?.data?.deviceType && res.data?.data?.deviceType !== null) {
        setSearchedItem(res?.data?.data);
        setQuotationInfo({
          ...quotationInfo,
          searchedSku: selectedProduct.sapSkuId,
          skuData: res.data.data,
        });
        navigate("/BundleAndItemSelection");
        setItemLoading(false);
      } else {
        const updateItemErrors = {
          ...itemErrors,
          skuId: true,
        };
        setItemErrors(updateItemErrors);
        setSearchedItem(undefined);
        setItemLoading(false);
        setErrorMessage({
          message: res.data.message || "Product not found",
          showMessage: true,
        });
      }
    } catch (error) {
      const updateItemErrors = {
        ...itemErrors,
        skuId: true,
      };
      setItemErrors(updateItemErrors);
      appInsights.trackException({
        error: error as Error,
        properties: {
          name: "Error while getting selected product details",
        },
      });
      setSearchedItem(undefined);
      setItemLoading(false);
      setErrorMessage({
        message: "Error retrieving product details",
        showMessage: true,
      });
    }
  };
  // Get inventory status display
  const getInventoryStatusDisplay = (availableInventory: number): string => {
    if (availableInventory <= 0) return "Out of Stock";
    if (availableInventory <= 25) return "Low Stock";
    return "In Stock";
  };

  // Get inventory status color based on status string
  const getInventoryStatusColorByStatus = (status: string): string => {
    switch (status.toLowerCase()) {
      case "in stock":
        return "#107c10"; // Green
      case "low stock":
      case "less stock":
        return "#a65a00"; // Dark orange - WCAG AA compliant
      case "out of stock":
        return "#d13438"; // Red
      default:
        return "#605e5c"; // Default gray
    }
  };

  // Get inventory status background color
  const getInventoryStatusBackgroundColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case "in stock":
        return "#f3f9f1"; // Light green background
      case "low stock":
      case "less stock":
        return "#fff8e1"; // Light yellow background
      case "out of stock":
        return "#fdf2f2"; // Light red background
      default:
        return "#faf9f8"; // Default light gray
    }
  };

  // Get inventory status color (legacy function for number-based)
  const getInventoryStatusColor = (availableInventory: number): string => {
    if (availableInventory <= 0) return "#d13438"; // Red
    if (availableInventory <= 25) return "#a65a00"; // Dark orange - WCAG AA compliant
    return "#107c10"; // Green
  };

  // Get actual color value based on color name
  const getColorValue = (colorName: string): string => {
    if (!colorName) return "#cccccc"; // Default gray for unknown colors
    
    const color = colorName.toLowerCase().trim();
    
    // Common color mappings
    const colorMap: { [key: string]: string } = {
      // Basic colors
      'black': '#000000',
      'white': '#ffffff',
      'gray': '#808080',
      'grey': '#808080',
      'silver': '#c0c0c0',
      'red': '#ff0000',
      'blue': '#0000ff',
      'green': '#008000',
      'yellow': '#ffff00',
      'orange': '#ffa500',
      'purple': '#800080',
      'pink': '#ffc0cb',
      'brown': '#a52a2a',
      'gold': '#ffd700',
      'navy': '#000080',
      'maroon': '#800000',
      'lime': '#00ff00',
      'cyan': '#00ffff',
      'magenta': '#ff00ff',
      
      // Microsoft Surface specific colors
      'platinum': '#e5e4e2',
      'cobalt': '#0047ab',
      'burgundy': '#800020',
      'sage': '#9cae53',
      'ice': '#d6f5ff',
      'sandstone': '#d2b48c',
      'graphite': '#41424c',
      'midnight': '#191970',
      'forest': '#228b22',
      'poppy': '#ff4500',
      'alcantara': '#4a4a4a',
      
      // Variations and combinations
      'light gray': '#d3d3d3',
      'dark gray': '#696969',
      'light grey': '#d3d3d3',
      'dark grey': '#696969',
      'space gray': '#8c8c8c',
      'space grey': '#8c8c8c',
      'rose gold': '#e8b4a0',
      'matte black': '#28282b',
      'deep blue': '#003366',
      'sky blue': '#87ceeb',
      'forest green': '#228b22',
      'olive green': '#808000',
      'bright red': '#ff0000',
      'coral': '#ff7f50',
      'lavender': '#e6e6fa',
      'mint': '#98ff98',
      'cream': '#fffdd0',
      'ivory': '#fffff0',
      'charcoal': '#36454f',
      'slate': '#708090',
      'steel': '#4682b4',
      'bronze': '#cd7f32',
      'copper': '#b87333',
      'chrome': '#c0c0c0'
    };
    
    // Try exact match first
    if (colorMap[color]) {
      return colorMap[color];
    }
    
    // Try partial matches for compound color names
    for (const [key, value] of Object.entries(colorMap)) {
      if (color.includes(key) || key.includes(color)) {
        return value;
      }
    }
    
    // Default to a neutral gray if no match found
    return '#cccccc';
  };

  const resetItemData = () => {
    setErrorMessage(InitialStateOfResponseMessage);
    setItemErrors(ItemErrorsDefaultState);
    setItemFormData(DefaultItemFormData);
    setSearchingSku("");
    setItemLoading(false);
    setSearchResults([]);
    setShowSearchDropdown(false);
    setSearchLoading(false);
    setCurrentPage(1);
    setTotalResults(0);
    setTotalPages(0);
    setHasNextPage(false);
    setHasPreviousPage(false);
    dismissItemPanel();
  };
  const onSearchItem = (newValue: any) => {
    if (searchingSku) {
      setItemLoading(true);
      setItemFormData(DefaultItemFormData);
      if (itemErrors.skuId) {
        const updateItemErrors = {
          ...itemErrors,
          skuId: false,
        };
        setItemErrors(updateItemErrors);
      }
      appInsights.trackEvent({
        name: "In QuotationItems.tsx, started get sku detail API call on item change.",
        properties: { selectedItem: searchingSku },
      });
      const searchProductPayload: ISearchProductPayload = {
        sapId: searchingSku,
        bigId: "",
        market: quotationInfo.customer.customerMarket ?? "US",
        languageCode: quotationInfo.customer.languageCode ?? "en-US",
        customerId: quotationInfo.customer.id
      };
      QuotationDetailService.getSearchedProductDetails(searchProductPayload)
        .then((res) => {
          appInsights.trackEvent({
            name: "In AddItemPanelComponent.tsx, item record has been obtained successfully on the basis of searched SKU.",
            properties: {
              component: `${window.history.state?.state?.actionName} Quotation Items`,
            },
          });
          if (
            res.data?.data?.deviceType &&
            res.data?.data?.deviceType !== null
          ) {
            setSearchedItem(res?.data?.data);
            setQuotationInfo({
              ...quotationInfo,
              searchedSku: searchingSku,
              skuData: res.data.data,
            });
            navigate("/BundleAndItemSelection");
            setItemLoading(false);
          } else {
            const updateItemErrors = {
              ...itemErrors,
              skuId: true,
            };
            setItemErrors(updateItemErrors);
            setSearchedItem(undefined);
            setItemLoading(false);
            setErrorMessage({
              message: res.data.message,
              showMessage: true,
            });
          }
        })
        .catch((error) => {
          const updateItemErrors = {
            ...itemErrors,
            skuId: true,
          };
          setItemErrors(updateItemErrors);
          appInsights.trackException({
            error,
            properties: {
              name: "Error while getting sku details in QuotationItems.tsx",
            },
          });
          setSearchedItem(undefined);
          setItemLoading(false);
        });
    }
  };
  const getAPIStaticData = () => {
    setQuotationInfo({
      ...quotationInfo,
      searchedSku: searchingSku,
      skuData: {
        deviceType: "Bundle",
        standaloneDevices: [
          {
            productId: "8WG5VQP0X389",
            sapItemId: "QJX-00009",
            skuId: "L12P",
            availabilityId: "8SM8Q04LWDSK",
            productTitle: "Surface Pro Keyboard til virksomheder",
            availableInventory: 3,
            msrp: 159.95,
            msrpExVat: 135.95,
            discountedPrice: 0,
            isPreOrder: false,
            taxCode: "",
            taxType:"",
          },
          {
            productId: "8WG5VQP0X389",
            sapItemId: "QJX-00009",
            skuId: "34S3",
            availabilityId: "8NK47563KTST",
            productTitle: "Surface Pro Keyboard for Business",
            availableInventory: 3,
            msrp: 159.95,
            msrpExVat: 135.95,
            discountedPrice: 0,
            isPreOrder: true,
            taxCode: "",
            taxType:"",
          },
          {
            productId: "8WG5VQP0X389",
            sapItemId: "QJX-00009",
            skuId: "KKR1",
            availabilityId: "8QTZNWDDV4CK",
            productTitle: "Surface Pro Keyboard for Business",
            availableInventory: 3,
            msrp: 359.95,
            msrpExVat: 305.95,
            discountedPrice: 0,
            isPreOrder: false,
            taxCode: "",
            taxType:"",
          },
          {
            productId: "8WG5VQP0X389",
            sapItemId: "QJX-00009",
            skuId: "VGQ3",
            availabilityId: "941R1D1KR4L1",
            productTitle: "Surface Pro Keyboard for Business",
            availableInventory: 3,
            msrp: 459.95,
            discountedPrice: 0,
            isPreOrder: false,
            taxCode: "",
            taxType:"",
          },
          {
            productId: "8WG5VQP0X389",
            sapItemId: "QJX-00009",
            skuId: "ZCQL",
            availabilityId: "8QWNXH3L0GG4",
            productTitle: "Surface Pro Keyboard for Business",
            availableInventory: 3,
            msrp: 309.95,
            discountedPrice: 0,
            isPreOrder: true,
            taxCode: "",
            taxType:"",
          },
        ],
        bundles: [
          {
            bundleTitle: "BuyMGetN 1",
            bundleId: "8WG5VQP0X2Z3",
            bundleSlots: null,
            message: null,
          },
          {
            bundleTitle: "BuyMGetN 2",
            bundleId: "8WG5VQP0X2Z4",
            bundleSlots: null,
            message: null,
          },
          {
            bundleTitle: "BuyMGetN 3",
            bundleId: "8WG5VQP0X2Z5",
            bundleSlots: null,
            message: null,
          },
          {
            bundleTitle: "BuyMGetN 4",
            bundleId: "8WG5VQP0X2Z6",
            bundleSlots: null,
            message: null,
          },
        ],
        standaloneMessages: [],
        bundleMessages: [],
      },
    });
    navigate("/BundleAndItemSelection");
  };
  const onClickSaveItem = () => {
    const newQuoteLineItem: IItemDetailEntity = {
      ...itemFormData,
      discount: itemFormData.discount,
      netAmount: parseFloat(
        (itemFormData.unitPrice * itemFormData.quantity).toFixed(2)
      ),
      lineItemType: LineItemType.Add,
      lineItemIndex:
        quotationInfo.quoteLineItems.length > 0
          ? quotationInfo.quoteLineItems[
              quotationInfo.quoteLineItems.length - 1
            ].lineItemIndex + 1
          : quotationInfo.quoteLineItems.length,
    };
    quotationInfo.quoteLineItems.push(newQuoteLineItem);
    const netAmountSum = quotationInfo.quoteLineItems.reduce(
      (accumulator: number, currentItem: IItemDetailEntity) =>
        accumulator + currentItem.netAmount,
      0
    );
    setQuotationInfo({
      ...quotationInfo,
      quoteLineItems: quotationInfo.quoteLineItems,
      total: netAmountSum > 0 ? netAmountSum.toFixed(2).toLocaleString() : "",
    });
    resetItemData();
  };
  return (
    <>
      <style>{`
        /* Hide the overlay/backdrop that appears when panel opens */
        .ms-Overlay {
          background-color: transparent !important;
          cursor: default !important;
          pointer-events: none !important;
          display: none !important;
        }
        
        div[data-portal-element="true"] .ms-Panel {
          position: fixed !important;
          top: 48px !important;
          margin: 0 !important;
          left: auto  !important;
          right: 0 !important;
          width: 90% !important;
          max-width: 1312px !important;
        }
        .ms-Panel[role="dialog"] {
          position: fixed !important;
          top: 48px !important;
          margin: 0 !important;
          left: auto !important;
          right: 0 !important;
          width: 90% !important;
          max-width: 1312px !important;
        }
        .ms-Panel.ms-Panel--hasCloseButton {
          position: fixed !important;
          top: 48px !important;
          margin: 0 !important;
          left: auto !important;
          right: 0 !important;
          width: 90% !important;
          max-width: 1312px !important;
        }
        .add-item-panel-container .ms-Panel {
          position: fixed !important;
          top: 48px !important;
          margin: 0 !important;
          left: auto !important;
          right: 0 !important;
          width: 90% !important;
          max-width: 1312px !important;
        }
        .add-item-panel-container .ms-Panel-main {
          position: relative !important;
          max-width: 90% !important;
          width: 90% !important;
          min-width: 800px !important;
          box-shadow: none !important;
        }
        .add-item-panel-container .ms-Panel-content {
          position: relative !important;
          overflow-x: auto !important;
        }
        .add-item-panel-container .ms-Panel-commands {
          position: relative !important;
        }
        .add-item-panel-container .ms-Panel-contentInner {
          position: relative !important;
          overflow-x: auto !important;
          width: 100% !important;
        }
        .ms-Panel[data-is-visible="true"] {
          position: fixed !important;
          top: 48px !important;
          margin: 0 !important;
          left: auto !important;
          right: 0 !important;
          width: 90% !important;
          max-width: 1312px !important;
        }
        @media (max-width: 1366px) {
          .add-item-panel-container .ms-Panel-main {
            min-width: 700px !important;
          }
        }
      `}</style>
      <div 
        className="add-item-panel-container"
        style={{ 
          position: "fixed",
          left: "1100px", // Moved panel significantly to the left to ensure Color column is fully visible
          top: "48px", // Positioned to just touch the blue header line
          transform: "translateY(0)",
          width: "1312px", // Reduced width to fit better on screen while showing all columns
          height: "auto",
          zIndex: 1000,
          pointerEvents: isItemPanelOpen ? "auto" : "none"
        }}
      > {/* Position panel for proper alignment with main content */}
      <Stack>
        <CoherencePanel
          panelSize="96%"
          headerText={`${ADD_ACTION} Item`}
          titleText={`${ADD_ACTION} Item`}
          isOpen={isItemPanelOpen}
          onDismiss={resetItemData}
          onAbort={resetItemData}
          isFooterAtBottom={true}
          isBlocking={false}
          styles={OverridePanelPaddingStyle}
          // You MUST provide this prop! Otherwise screen readers will just say "button" with no label.
          closeButtonAriaLabel="Close"
        >
        {errorMessage.showMessage && (
          <MessageBar
            messageBarType={MessageBarType.error}
            onDismiss={() => setErrorMessage(InitialStateOfResponseMessage)}
          >
            {errorMessage.message}
          </MessageBar>
        )}
        <form 
          onClick={(e) => {
            // Check if click is inside search dropdown
            const searchDropdown = document.querySelector('.search-results-dropdown');
            if (searchDropdown && searchDropdown.contains(e.target as Node)) {
              e.stopPropagation();
            }
          }}
        >
          <Stack horizontal>            <Stack {...columnPropsWithHeight150}>
              <Stack
                horizontal
                horizontalAlign="start"
                tokens={stackTokensWithGap5}
                style={{ position: "relative", width: "100%", maxWidth: "100%" }} // Remove fixed margin to improve responsiveness
              >                <div style={{ position: "relative", flex: 1, width: "100%" }}>
                  <SearchBox
                    styles={{
                      ...searchBoxStylesWithHiddenIcon,
                      root: {
                        width: "100%", // Use full available width
                        maxWidth: "2096px", // Increased by 288px (4 inches) from 1808px
                        paddingRight: "8px" // Reduced padding since icon is now external
                      },
                      field: {
                        paddingRight: "8px !important", // Reduced padding for the input field
                        borderTopRightRadius: "0px !important", // Remove right border radius to connect with icon
                        borderBottomRightRadius: "0px !important",
                        backgroundColor: "#ffffff !important",
                        cursor: "text !important"
                      }
                    }}
                    placeholder={!FeatureFlags.enableProductSearch ? "Type search term and click search icon (Enter key search disabled)" : "Type search term and press Enter to search (e.g., 'surface', 'laptop')..."}
                    value={searchingSku}
                    disabled={false}
                    onEscape={(ev) => {
                      setSearchingSku("");
                      setItemFormData(DefaultItemFormData);
                      setItemLoading(false);
                      setShowSearchDropdown(false);
                    }}
                    onClear={(ev) => {
                      setSearchingSku("");
                      setItemFormData(DefaultItemFormData);
                      setItemLoading(false);
                      setShowSearchDropdown(false);
                    }}
                    onChange={handleSearchChange}
                    onSearch={(newValue) => {
                      console.log("SearchBox onSearch triggered with newValue:", newValue, "searchingSku:", searchingSku);
                      console.log("FeatureFlags.enableProductSearch:", FeatureFlags.enableProductSearch);
                      if (FeatureFlags.enableProductSearch) {
                        console.log("Calling handleSearchSubmit with:", newValue || searchingSku);
                        handleSearchSubmit(newValue || searchingSku);
                      } else {
                        console.log("Feature flag disabled, not calling handleSearchSubmit");
                      }
                    }}
                  />
                  
                  {/* Search Results Dropdown - Grid/List View */}
                  {showSearchDropdown && searchResults.length > 0 && (
                    <div 
                      className="search-results-dropdown" 
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{
                        position: "absolute",
                        top: "calc(100% + 4px)", // Add 4px gap to prevent overlap with search icon
                        left: 0, // Align with left edge of search box
                        width: "calc(100% + 650px)", // Increased width to accommodate the left extension
                        maxWidth: "1400px", // Increased max width
                        background: "#ffffff",
                        border: "1px solid #c8c6c4",
                        borderRadius: "2px",
                        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                        zIndex: 999, // Reduced from 1000 to prevent overlap with panel footer
                        display: "flex",
                        flexDirection: "column",
                        overflow: "visible", // Allow content to be visible - restore Color column
                        overflowX: "hidden" // Hide horizontal scrollbar only
                      }}>
                      {/* Filters and Sorting Header */}
                      <div 
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{
                          padding: "8px 12px",
                          borderBottom: "1px solid #edebe9",
                          backgroundColor: "#ffffff",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexShrink: 0,
                          flexWrap: "wrap",
                          gap: "8px",
                          width: "calc(100% + 250px)" // Updated to match actual grid width
                        }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setShowFilters(!showFilters);
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            style={{
                              background: showFilters ? "#deecf9" : "transparent",
                              border: "1px solid #c8c6c4",
                              borderRadius: "2px",
                              padding: "4px 8px",
                              cursor: "pointer",
                              color: showFilters ? "#005a9e" : "#605e5c",
                              fontSize: "11px"
                            }}
                            title="Toggle Filters"
                          >
                            🔍 Filters
                          </button>
                          
                          {/* Sort Dropdown */}
                          <select
                            aria-label="Sort by"
                            value={sortBy}
                            onChange={(e) => {
                              e.stopPropagation();
                              setSortBy(e.target.value);
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                            }}
                            style={{
                              border: "1px solid #c8c6c4",
                              borderRadius: "2px",
                              padding: "4px 6px",
                              fontSize: "11px",
                              backgroundColor: "#ffffff"
                            }}
                          >
                            <option value="name">Sort: Name</option>
                            <option value="inventory">Sort: Inventory</option>
                            <option value="screenSize">Sort: Screen Size</option>
                            <option value="processor">Sort: Processor</option>
                          </select>
                          
                          {/* Sort Order */}
                          <button
                            type="button"
                            disabled={sortBy === ""}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (sortBy !== "") {
                                setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                              }
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            style={{
                              background: "transparent",
                              border: "1px solid #c8c6c4",
                              borderRadius: "2px",
                              padding: "4px 6px",
                              cursor: sortBy === "" ? "not-allowed" : "pointer",
                              color: sortBy === "" ? "#a19f9d" : "#605e5c",
                              fontSize: "11px",
                              opacity: sortBy === "" ? 0.6 : 1
                            }}
                            title={sortBy === "" ? "Select a sort option first" : `Sort ${sortOrder === "asc" ? "Descending" : "Ascending"}`}
                          >
                            {sortOrder === "asc" ? "↑" : "↓"}
                          </button>
                          
                          {/* Reset Filters */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              resetFilters();
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            style={{
                              background: "transparent",
                              border: "1px solid #c8c6c4",
                              borderRadius: "2px",
                              padding: "4px 6px",
                              cursor: "pointer",
                              color: "#605e5c",
                              fontSize: "11px"
                            }}
                            title="Reset All Filters"
                          >
                            Reset
                          </button>
                        </div>
                      </div>

                      {/* Filter Controls - Collapsible */}
                      {showFilters && (
                        <div 
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          style={{
                            borderBottom: "1px solid #edebe9",
                            backgroundColor: "#f8f9fa",
                            flexShrink: 0,
                            width: "calc(100% + 250px)" // Updated to match actual grid width
                          }}>
                          {/* First Row - Primary Filters */}
                          <div 
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            style={{
                              padding: "8px 12px",
                              display: "flex",
                              gap: "12px",
                              flexWrap: "wrap",
                              alignItems: "center",
                              width: "calc(100% + 250px)" // Updated to match actual grid width
                            }}>
                            {/* Screen Size Filter */}
                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <label style={{ fontSize: "11px", color: "#605e5c", fontWeight: 500 }}>Screen:</label>
                              <select
                                aria-label="Filter by screen size"
                                value={screenSizeFilter}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setScreenSizeFilter(e.target.value);
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                }}
                                style={{
                                  border: "1px solid #c8c6c4",
                                  borderRadius: "2px",
                                  padding: "2px 4px",
                                  fontSize: "11px",
                                  backgroundColor: "#ffffff"
                                }}
                              >
                                <option value="all">All Sizes</option>
                                {getUniqueValues(searchResults, 'screenSize').map(size => (
                                  <option key={size} value={size}>{size}</option>
                                ))}
                              </select>
                            </div>

                            {/* Processor Filter */}
                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <label style={{ fontSize: "11px", color: "#605e5c", fontWeight: 500 }}>Processor:</label>
                              <select
                                aria-label="Filter by processor"
                                value={processorFilter}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setProcessorFilter(e.target.value);
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                }}
                                style={{
                                  border: "1px solid #c8c6c4",
                                  borderRadius: "2px",
                                  padding: "2px 4px",
                                  fontSize: "11px",
                                  backgroundColor: "#ffffff"
                                }}
                              >
                                <option value="all">All Processors</option>
                                {getUniqueValues(searchResults, 'processor').map(processor => (
                                  <option key={processor} value={processor}>{processor}</option>
                                ))}
                              </select>
                            </div>

                            {/* Status Filter */}
                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <label style={{ fontSize: "11px", color: "#605e5c", fontWeight: 500 }}>Status:</label>
                              <select
                                aria-label="Filter by status"
                                value={statusFilter}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setStatusFilter(e.target.value);
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                }}
                                style={{
                                  border: "1px solid #c8c6c4",
                                  borderRadius: "2px",
                                  padding: "2px 4px",
                                  fontSize: "11px",
                                  backgroundColor: "#ffffff"
                                }}
                              >
                                <option value="all">All Status</option>
                                {getUniqueValues(searchResults, 'inventoryStatus').map(status => (
                                  <option key={status} value={status}>{status}</option>
                                ))}
                              </select>
                            </div>

                            {/* SAP ID Filter */}
                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <label style={{ fontSize: "11px", color: "#605e5c", fontWeight: 500 }}>SAP ID:</label>
                              <select
                                aria-label="Filter by SAP ID"
                                value={sapIdFilter}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setSapIdFilter(e.target.value);
                                  if (e.target.value !== "all") {
                                    setCustomSapId(""); // Clear custom input when dropdown is used
                                  }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                }}
                                style={{
                                  border: "1px solid #c8c6c4",
                                  borderRadius: "2px",
                                  padding: "2px 4px",
                                  fontSize: "11px",
                                  backgroundColor: "#ffffff",
                                  marginRight: "4px"
                                }}
                              >
                                <option value="all">All SAP IDs</option>
                                {getUniqueValues(searchResults, 'sapSkuId').map(sapId => (
                                  <option key={sapId} value={sapId}>{sapId}</option>
                                ))}
                              </select>
                              <span style={{ fontSize: "11px", color: "#605e5c", margin: "0 4px" }}>or</span>
                              <input
                                type="text"
                                placeholder="Enter SAP ID..."
                                value={customSapId}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setCustomSapId(e.target.value);
                                  if (e.target.value.trim() !== "") {
                                    setSapIdFilter("all"); // Clear dropdown when custom input is used
                                  }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                }}
                                style={{
                                  border: "1px solid #c8c6c4",
                                  borderRadius: "2px",
                                  padding: "2px 6px",
                                  fontSize: "11px",
                                  backgroundColor: "#ffffff",
                                  minWidth: "100px",
                                  maxWidth: "120px"
                                }}
                              />
                            </div>
                          </div>

                          {/* Second Row - Additional Filters */}
                          <div 
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            style={{
                              padding: "8px 12px",
                              paddingTop: "4px",
                              display: "flex",
                              gap: "12px",
                              flexWrap: "wrap",
                              alignItems: "center",
                              borderTop: "1px solid #e1dfdd",
                              width: "calc(100% + 340px)" // Updated to match actual grid width with MSRPEXVAT column
                            }}>
                            {/* Product Title Filter */}
                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <label style={{ fontSize: "11px", color: "#605e5c", fontWeight: 500 }}>Product Title:</label>
                              <input
                                type="text"
                                placeholder="Search title..."
                                value={productTitleFilter}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setProductTitleFilter(e.target.value);
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                }}
                                style={{
                                  border: "1px solid #c8c6c4",
                                  borderRadius: "2px",
                                  padding: "2px 6px",
                                  fontSize: "11px",
                                  backgroundColor: "#ffffff",
                                  minWidth: "120px",
                                  maxWidth: "140px"
                                }}
                              />
                            </div>

                            {/* Product ID Filter */}
                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <label style={{ fontSize: "11px", color: "#605e5c", fontWeight: 500 }}>Product ID:</label>
                              <input
                                type="text"
                                placeholder="Search ID..."
                                value={productIdFilter}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setProductIdFilter(e.target.value);
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                }}
                                style={{
                                  border: "1px solid #c8c6c4",
                                  borderRadius: "2px",
                                  padding: "2px 6px",
                                  fontSize: "11px",
                                  backgroundColor: "#ffffff",
                                  minWidth: "100px",
                                  maxWidth: "120px"
                                }}
                              />
                            </div>


                            {/* Offering Type Filter */}
                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <label style={{ fontSize: "11px", color: "#605e5c", fontWeight: 500 }}>Offering Type:</label>
                              <select
                                aria-label="Filter by offering type"
                                value={preOrderFilter}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setPreOrderFilter(e.target.value);
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                }}
                                style={{
                                  border: "1px solid #c8c6c4",
                                  borderRadius: "2px",
                                  padding: "2px 4px",
                                  fontSize: "11px",
                                  backgroundColor: "#ffffff"
                                }}
                              >
                                <option value="all">All</option>
                                <option value="true">Preorder</option>
                                <option value="false">Generally Available</option>
                              </select>
                            </div>

                            {/* Color Filter */}
                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <label style={{ fontSize: "11px", color: "#605e5c", fontWeight: 500 }}>Color:</label>
                              <select
                                aria-label="Filter by color"
                                value={colorFilter}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setColorFilter(e.target.value);
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                }}
                                style={{
                                  border: "1px solid #c8c6c4",
                                  borderRadius: "2px",
                                  padding: "2px 4px",
                                  fontSize: "11px",
                                  backgroundColor: "#ffffff"
                                }}
                              >
                                <option value="all">All Colors</option>
                                {getUniqueValues(searchResults, 'color').map(color => (
                                  <option key={color} value={color}>{color}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Results Container - Removed outer scrollable wrapper to prevent double scrollbars */}
                      <div 
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{ 
                          padding: "0px", // Remove all padding
                          margin: "0px", // Remove all margin
                          display: "flex",
                          flexDirection: "column",
                          flex: 1, // Take remaining space
                          overflow: "visible" // Allow content to be visible - restore Color column
                        }}>
                        {/* Table View with Fixed Header and Scrollable Data */}
                        <div style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "calc(100% + 280px)", // Increased from 190px to 280px to accommodate MSRPEXVAT column
                          height: "100%",
                          margin: "0px", // Remove any margin
                          padding: "0px", // Remove any padding
                          overflow: "visible" // Allow content to be visible - restore Color column
                        }}>
                          {/* Fixed Grid Header - Adjusted column widths to fit all columns in view */}
                          <div style={{
                            display: "grid",
                            gridTemplateColumns: "350px 200px 100px 60px 90px 80px 110px 85px 60px 85px 60px", // Moved MSRP Ex VAT after SAP ID, made Color column visible
                            gap: "0px", // Removed gap to allow for proper borders
                            alignItems: "center",
                            padding: "0px", // Removed padding to allow for proper borders
                            backgroundColor: "#f8f9fa",
                            border: "1px solid #d1d1d1",
                            borderBottom: "2px solid #c8c6c4",
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#323130",
                            height: "32px", // Increased height for better visibility
                            position: "sticky",
                            top: "0",
                            zIndex: 10,
                            flexShrink: 0,
                            marginTop: "0px" // Ensure no top margin
                          }}>
                            <div style={{ textAlign: "left", paddingLeft: "8px", height: "100%", display: "flex", alignItems: "center" }}>Product Title</div>
                            <div style={{ textAlign: "center", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}></div> {/* Spacing column before Product ID - no border */}
                            <div style={{ textAlign: "left", paddingLeft: "8px", borderLeft: "1px solid #d1d1d1", borderRight: "1px solid #d1d1d1", height: "100%", display: "flex", alignItems: "center" }}>Product ID</div>
                            <div style={{ textAlign: "center", borderRight: "1px solid #d1d1d1", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>SAP ID</div>
                            <div style={{ textAlign: "center", borderRight: "1px solid #d1d1d1", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>MSRP Ex VAT</div>
                            <div style={{ textAlign: "center", borderRight: "1px solid #d1d1d1", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>InventoryStatus</div>
                            <div style={{ textAlign: "center", borderRight: "1px solid #d1d1d1", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", whiteSpace: "nowrap" }}>Offering Type</div>
                            <div style={{ textAlign: "center", borderRight: "1px solid #d1d1d1", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>Size</div>
                            <div style={{ textAlign: "center", borderRight: "1px solid #d1d1d1", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>Processor</div>
                            <div style={{ textAlign: "center", borderRight: "1px solid #d1d1d1", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>Color</div>
                          </div>

                          {/* Data Rows Container - All rows visible without scrolling */}
                          <div role="listbox" aria-label="Product search results" tabIndex={0} style={{
                            overflow: "visible", // Show all content including Color column
                            marginTop: "2px", // Add small margin to separate from sticky header
                            paddingBottom: "8px" // Add bottom padding to prevent last row overlap
                          }}
                          onKeyDown={(e) => {
                            const options = Array.from(e.currentTarget.querySelectorAll('[role="option"]')) as HTMLElement[];
                            if (options.length === 0) return;
                            const focused = document.activeElement as HTMLElement;
                            let currentIndex = options.indexOf(focused);
                            if (currentIndex === -1) currentIndex = 0;
                            let nextIndex = currentIndex;
                            if (e.key === "ArrowDown") {
                              e.preventDefault();
                              nextIndex = (currentIndex + 1) % options.length;
                            } else if (e.key === "ArrowUp") {
                              e.preventDefault();
                              nextIndex = (currentIndex - 1 + options.length) % options.length;
                            } else {
                              return;
                            }
                            options.forEach((option, i) => {
                              option.setAttribute('tabindex', i === nextIndex ? '0' : '-1');
                              option.setAttribute('aria-selected', i === nextIndex ? 'true' : 'false');
                            });
                            options[nextIndex].focus();
                          }}>
                            {isCommittedSearch && getPaginatedResults().map((result: IProductSearchResult, index: number) => {
                              const uniqueRowId = `${result.productId}-${index}`;
                              return (
                              <div
                                key={uniqueRowId}
                              className="search-result-row"
                              style={{
                                borderBottom: index < getPaginatedResults().length - 1 ? "1px solid #edebe9" : "none",
                                cursor: "pointer",
                                transition: "all 0.1s ease",
                                backgroundColor: "#ffffff",
                                fontSize: "10px", // Increased from 9px to 10px for better readability
                                marginTop: index === 0 ? "4px" : "1px", // Add small margin between rows to prevent overlap
                                marginBottom: "1px" // Add bottom margin for better row separation
                              }}
                              onClick={() => handleProductSelection(result)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#f3f2f1";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "#ffffff";
                              }}
                              role="option"
                              tabIndex={index === 0 ? 0 : -1}
                              aria-selected={index === 0 ? "true" : "false"}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handleProductSelection(result);
                                }
                              }}
                            >
                              {/* Single Row - Grid Layout with Product Title */}
                              <div style={{
                                display: "grid",
                                gridTemplateColumns: "350px 200px 100px 60px 90px 80px 110px 85px 60px 85px 60px", // Moved MSRP Ex VAT after SAP ID, made Color column visible
                                gap: "0px", // Removed gap to allow for proper borders
                                alignItems: "flex-start", // Align to top for proper text wrapping
                                padding: "0px", // Removed padding to allow for proper borders
                                minHeight: "32px", // Increased height to match header
                                height: "auto", // Auto height for text wrapping
                                border: "1px solid #d1d1d1",
                                borderTop: "none", // Remove top border to avoid double borders
                                backgroundColor: index % 2 === 0 ? "#ffffff" : "#f9f9f9" // Alternating row colors
                              }}>
                                {/* Product Title - Left aligned with expand option */}
                                <div style={{
                                  fontSize: "9px", // Updated to match Product ID font size
                                  fontWeight: 600,
                                  color: "#323130",
                                  textAlign: "left",
                                  paddingLeft: "8px", // Match header padding
                                  padding: "6px 8px", // Add consistent padding
                                  overflow: "visible", // Allow text to be fully visible
                                  display: "flex", // Flex display for better alignment
                                  alignItems: "center", // Center content vertically
                                  width: "100%", // Take full cell width
                                  height: "100%", // Take full cell height
                                  position: "relative",
                                  whiteSpace: "nowrap" // Prevent text wrapping - display whole title in same row
                                }}>
                                  <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "150%", // Ensure container stays within bounds
                                    overflow: "visible" // Allow text to extend beyond column
                                  }}>
                                    <span 
                                      style={{ 
                                        flex: 1, 
                                        minWidth: 0,
                                        width: "100%", // Auto width to accommodate full text
                                        overflow: "hidden", // prevent overflow
                                        textOverflow: "ellipsis", // Show ellipsis for overflow text                                        
                                        whiteSpace: "nowrap", // Prevent text wrapping
                                        lineHeight: "1.3"
                                      }}
                                      title={result.productTitle}
                                    >
                                      {result.productTitle}
                                    </span>
                                  </div>
                                </div>

                                {/* Spacing column before Product ID */}
                                <div style={{
                                  textAlign: "center",
                                  fontSize: "9px",
                                  color: "transparent",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center"
                                }}>
                                </div>

                                {/* Product ID */}
                                <div style={{ 
                                  fontSize: "9px", // Increased from 8px to 9px for better readability
                                  color: "#323130",
                                  textAlign: "left", // Changed from center to left alignment
                                  paddingLeft: "8px", // Consistent padding
                                  padding: "6px 8px", // Add consistent padding
                                  whiteSpace: "nowrap", // Prevent wrapping for autofit
                                  fontWeight: 600, // Match product title font weight
                                  borderLeft: "1px solid #d1d1d1", // Add border before Product ID
                                  borderRight: "1px solid #d1d1d1",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center"
                                }}>
                                  {result.productId}
                                </div>

                                {/* SAP ID */}
                                <div style={{ 
                                  fontSize: "9px", // Increased from 8px to 9px for better readability
                                  color: "#323130",
                                  textAlign: "center",
                                  padding: "6px 4px", // Add consistent padding
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  fontWeight: 600, // Match product title font weight
                                  borderRight: "1px solid #d1d1d1",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center"
                                }}>
                                  {result.sapSkuId}
                                </div>

                                {/* MSRP Ex VAT - Moved after SAP ID */}
                                <div style={{
                                  textAlign: "center",
                                  fontSize: "9px", // Increased from 8px to 9px for better readability
                                  color: "#323130",
                                  fontWeight: 600, // Match product title font weight
                                  whiteSpace: "nowrap",
                                  padding: "6px 4px", // Add consistent padding
                                  borderRight: "1px solid #d1d1d1",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center"
                                }}>
                                  ${result.msrpExVat ? result.msrpExVat.toFixed(2) : "0.00"}
                                </div>

                                {/* Inventory Count with Status in brackets */}
                                <div style={{ 
                                  textAlign: "center",
                                  fontSize: "9px", // Increased from 8px to 9px for better readability
                                  fontWeight: 600, // Match product title font weight
                                  color: getInventoryStatusColorByStatus(result.inventoryStatus),
                                  whiteSpace: "nowrap",
                                  padding: "6px 4px", // Add consistent padding
                                  borderRight: "1px solid #d1d1d1",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center"
                                }}>
                                  {result.inventory} ({result.inventoryStatus})
                                </div>

                                {/* IsPreOrder */}
                                <div style={{
                                  textAlign: "center",
                                  fontSize: "9px", // Increased from 8px to 9px for better readability
                                  color: "#323130", // Neutral color instead of conditional coloring
                                  fontWeight: 600, // Match product title font weight
                                  whiteSpace: "nowrap",
                                  padding: "6px 4px", // Add consistent padding
                                  borderRight: "1px solid #d1d1d1",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center"
                                }}>
                                  {result.isPreOrder ? "PreOrder" : "GA"}
                                </div>

                                {/* Screen Size */}
                                <div style={{
                                  textAlign: "center",
                                  fontSize: "9px", // Increased from 8px to 9px for better readability
                                  color: "#323130",
                                  fontWeight: 600, // Match product title font weight
                                  whiteSpace: "nowrap",
                                  padding: "6px 4px", // Add consistent padding
                                  borderRight: "1px solid #d1d1d1",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center"
                                }}>
                                  {result.screenSize || ""}
                                </div>

                                {/* Processor */}
                                <div style={{
                                  textAlign: "center",
                                  fontSize: "9px", // Increased from 8px to 9px for better readability
                                  color: "#323130",
                                  fontWeight: 600, // Match product title font weight
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  padding: "6px 4px", // Add consistent padding
                                  borderRight: "1px solid #d1d1d1",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center"
                                }} title={result.processor || ""}>
                                  {result.processor || ""}
                                </div>

                                {/* Color - Now visible and properly positioned */}
                                <div style={{
                                  textAlign: "center",
                                  fontSize: "9px", // Increased from 8px to 9px for better readability
                                  color: "#323130",
                                  fontWeight: 600, // Match product title font weight
                                  whiteSpace: "nowrap",
                                  padding: "6px 4px", // Add consistent padding
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "3px"
                                }}>
                                  {result.color && result.color !== "N/A" && result.color.toLowerCase() !== "unknown" && (
                                    <div 
                                      style={{
                                        width: "10px",
                                        height: "10px",
                                        borderRadius: "2px",
                                        backgroundColor: getColorValue(result.color),
                                        border: getColorValue(result.color) === '#ffffff' ? "1px solid #c8c6c4" : "1px solid #323130",
                                        flexShrink: 0,
                                        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)"
                                      }}
                                      title={`Color: ${result.color}`}
                                    />
                                  )}

                                  <span style={{ 
                                    overflow: "hidden", 
                                    textOverflow: "ellipsis",
                                    minWidth: 0,
                                    flex: 1
                                  }}>
                                    {result.color && result.color !== "N/A" && result.color.toLowerCase() !== "unknown" ? result.color : ""}
                                  </span>
                                </div>
                              </div>
                            </div>
                            );
                          })}
                          </div>
                        </div>
                      </div>

                      {/* Pagination and Results Count at Bottom */}
                      {isCommittedSearch && (
                        <div style={{
                          padding: "8px 12px",
                          borderTop: "1px solid #edebe9",
                          backgroundColor: "#faf9f8",
                          display: "flex",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          flexShrink: 0,
                          gap: "16px",
                          width: "calc(100% + 250px)" // Updated to match actual grid width
                        }}>
                          {/* Pagination Controls at Bottom Left */}
                          {(totalResults >= resultsPerPage || searchResults.length > 0) && (
                            <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                              <button
                                type="button"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1 || searchLoading}
                                style={{
                                  background: "none",
                                  border: "1px solid #c8c6c4",
                                  borderRadius: "2px",
                                  padding: "2px 6px",
                                  cursor: (currentPage === 1 || searchLoading) ? "not-allowed" : "pointer",
                                  color: (currentPage === 1 || searchLoading) ? "#a19f9d" : "#323130",
                                  fontSize: "11px"
                                }}
                              >
                                ‹ Prev
                              </button>
                              <span style={{ color: "#605e5c", margin: "0 4px", fontSize: "11px" }}>
                                Page {currentPage} of {totalPages > 0 ? totalPages : getClientSideTotalPages()}
                              </span>
                              <button
                                type="button"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage >= (totalPages > 0 ? totalPages : getClientSideTotalPages()) || searchLoading}
                                style={{
                                  background: "none",
                                  border: "1px solid #c8c6c4",
                                  borderRadius: "2px",
                                  padding: "2px 6px",
                                  cursor: (currentPage >= (totalPages > 0 ? totalPages : getClientSideTotalPages()) || searchLoading) ? "not-allowed" : "pointer",
                                  color: (currentPage >= (totalPages > 0 ? totalPages : getClientSideTotalPages()) || searchLoading) ? "#a19f9d" : "#323130",
                                  fontSize: "11px"
                                }}
                              >
                                Next ›
                              </button>
                            </div>
                          )}
                          
                          <span style={{ fontSize: "12px", color: "#605e5c", fontWeight: 500 }}>
                            {getPaginatedResults().length} results found (Total: {totalResults}, Page: {currentPage}/{totalPages > 0 ? totalPages : Math.ceil(totalResults / resultsPerPage) || 1})
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Search Loading Indicator */}
                  {searchLoading && (
                    <div style={{
                      position: "absolute",
                      top: "calc(100% + 4px)", // Add 4px gap to prevent overlap with search icon
                      left: 0,
                      width: "calc(100vw - 100px)",
                      maxWidth: "700px",
                      background: "#ffffff",
                      border: "1px solid #c8c6c4",
                      borderRadius: "2px",
                      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                      zIndex: 999, // Reduced from 1000 to prevent overlap with panel footer
                      minHeight: "450px", // Reduced from 600px to 450px for better proportions
                      maxHeight: "calc(100vh - 180px)", // Increased from 120px to 180px for better footer spacing
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <Spinner size={SpinnerSize.small} />
                      <div style={{ marginTop: "8px", fontSize: "12px", color: "#605e5c" }}>
                        Searching...
                      </div>
                    </div>
                  )}

                  {/* No Results Found */}
                  {showSearchDropdown && searchResults.length === 0 && !searchLoading && searchingSku.length >= 3 && (
                    <div style={{
                      position: "absolute",
                      top: "calc(100% + 4px)", // Add 4px gap to prevent overlap with search icon
                      left: 0,
                      width: "calc(100vw - 100px)", // Match main dropdown width
                      maxWidth: "1200px", // Match main dropdown max width
                      background: "#ffffff",
                      border: "1px solid #c8c6c4",
                      borderRadius: "2px",
                      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                      zIndex: 999, // Reduced from 1000 to prevent overlap with panel footer
                      minHeight: "450px", // Reduced from 600px to 450px for better proportions
                      maxHeight: "calc(100vh - 180px)", // Increased from 120px to 180px for better footer spacing
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <Icon iconName="SearchIssue" style={{ fontSize: "16px", color: "#605e5c", marginBottom: "4px" }} />
                      <div style={{ fontSize: "14px", fontWeight: 600 }}>
                        No results found
                      </div>
                      <div style={{ fontSize: "12px", color: "#605e5c" }}>
                        Try a different search term
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Search Icon - Aligned with the search text box */}
                <div
                  data-search-icon="true"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "0 2px 2px 0", // Only right side rounded to connect with search box
                    padding: "0",
                    height: "32px", // Match SearchBox height exactly
                    width: "32px",
                    cursor: "pointer",
                    marginLeft: "0px", // No gap - directly connected to search box
                    backgroundColor: "#ffffff",
                    border: "1px solid #c8c6c4",
                    borderLeft: "none", // Remove left border to seamlessly connect with search box
                    alignSelf: "flex-start", // Align to top of the flex container
                    marginTop: "0px", // Ensure no extra top margin
                    opacity: 1
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label="Search products"
                  onClick={() => {
                    handleSearchIconClick(searchingSku);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleSearchIconClick(searchingSku);
                    }
                  }}
                >
                  <Icon
                    iconName="Search"
                    style={{
                      color: "rgb(0, 90, 158)",
                      fontSize: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  />
                </div>
              </Stack>
              {itemLoading ? (
                <LoadingState
                  primaryText=""
                />
              ) : itemFormData.skuId ? (
                <>
                  <TextField
                    label="Product Name"
                    name="productName"
                    value={itemFormData?.productName}
                    styles={textFieldGray}
                    readOnly
                    required
                    onChange={(e, newValue) =>
                      setItemFormData({
                        ...itemFormData,
                        productName: newValue ?? "",
                      })
                    }
                  />
                  <TextField
                    label="Available Inventory"
                    name="reservedInventory"
                    type="number"
                    value={itemFormData?.availableInventory.toString()}
                    readOnly
                    styles={textFieldGray}
                    required
                  />
                  <TextField
                    styles={
                      itemErrors.skuId ? textFieldGray : textFieldStyles300
                    }
                    label="Quantity"
                    name="quantity"
                    type="number"
                    errorMessage={
                      itemFormData.quantity <= 0 ? "Invalid Quantity" : ""
                    }
                    min={1}
                    readOnly={itemErrors.skuId ? true : false}
                    value={itemFormData?.quantity?.toString()}
                    required
                    onChange={(e, newValue) => {
                      setItemFormData({
                        ...itemFormData,
                        quantity: parseInt(newValue!),
                      });
                    }}
                  />
                  
                  {/* OK and Cancel buttons moved up for better visibility */}
                  <Stack style={{
                    marginTop: "16px",
                    marginBottom: "16px",
                    paddingTop: "12px",
                    paddingBottom: "12px",
                    borderTop: "2px solid #0078d4",
                    backgroundColor: "#f3f9ff",
                    padding: "16px",
                    borderRadius: "6px",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"
                  }}>
                    <Stack horizontal horizontalAlign="center" tokens={{ childrenGap: 12 }}>
                      <PrimaryButton
                        text="OK"
                        type="button"
                        onClick={onClickSaveItem}
                        style={buttonStyleWithWidth100}
                        disabled={
                          itemFormData.skuId !== "" && itemFormData.quantity >= 1
                            ? false
                            : true
                        }
                      />
                      <DefaultButton
                        text="Cancel"
                        type="button"
                        onClick={resetItemData}
                        style={buttonStyleWithWidth100}
                      />
                    </Stack>
                  </Stack>
                  
                  <TextField
                    label="MSRP"
                    name="unitPrice"
                    type="number"
                    value={itemFormData?.unitPrice.toString()}
                    required
                    readOnly
                    styles={textFieldGray}
                    onChange={(e, newValue) =>
                      setItemFormData({
                        ...itemFormData,
                        unitPrice: newValue ? parseFloat(newValue) : 0,
                      })
                    }
                  />
                </>
              ) : null}
            </Stack>
          </Stack>
        </form>
      </CoherencePanel>
      {searchedItem?.deviceType && <BundleSelection />}
    </Stack>
      </div> {/* Close the main container div */}
    </>
  );
};


